/* Homepage hero background: an ASCII wave-interference field.

   A grid of monospace glyphs covers the hero; each cell's brightness is the
   sum of a few slow sine waves radiating from random points, mapped onto a
   ramp of characters (space -> @) and drawn in translucent white over the
   hero's dark gradient. Replaces the old d3 globe (index, bfcm, aiLandingPage),
   so those pages no longer need d3/topojson at all.

   Performance: brightness is quantised to a few levels and cells are bucketed
   by level so fillStyle is set once per bucket rather than per glyph (measured
   ~40% cheaper than per-cell styling; a drawImage sprite atlas was tried and
   is ~2x slower than fillText in Chrome); each cell's distance to every wave
   source is computed once per resize; and the field redraws at most 30x/s
   (the motion is slow enough that more buys nothing). Together that's roughly
   3x less work per second than the original per-cell fillText at 60fps.

   Required from main.js and run on every page; it does nothing unless a
   #hero-waves / canvas.ascii-waves element is present. */
(function () {
  /* One instance per canvas: the homepage hero (#hero-waves) plus any section
     that opts in with <canvas class="ascii-waves"> inside a positioned,
     overflow-hidden parent. */
  var canvases = document.querySelectorAll('#hero-waves, canvas.ascii-waves');
  for (var c = 0; c < canvases.length; c++) init(canvases[c]);

  function init(canvas) {
    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    var CHARS = ' .·:;-=+*#@'; // darkest to brightest
    var FONT_SIZE = 14;
    var CELL_W = FONT_SIZE * 0.6; // 8.4px — width of one monospace glyph
    var CELL_H = FONT_SIZE;
    var FONT =
      FONT_SIZE +
      'px "SF Mono", Menlo, Consolas, "Liberation Mono", "Courier New", monospace';
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var LEVELS = 32; // brightness steps
    var FRAME_INTERVAL = 1000 / 30;

    /* Per level: the glyph and its translucent-white fill (alpha baked in).
       Levels that map to a space are marked empty and skipped. */
    var levelChar = new Array(LEVELS);
    var levelStyle = new Array(LEVELS);
    var levelEmpty = new Uint8Array(LEVELS);
    (function () {
      var maxIdx = CHARS.length - 1;
      for (var i = 0; i < LEVELS; i++) {
        var n = i / (LEVELS - 1);
        var idx = Math.floor(n * maxIdx);
        if (idx === 0) {
          levelEmpty[i] = 1;
          continue;
        }
        levelChar[i] = CHARS[idx];
        levelStyle[i] = 'rgba(255, 255, 255, ' + (0.12 + 0.2 * n).toFixed(3) + ')';
      }
    })();
    var buckets = [];
    for (var bi = 0; bi < LEVELS; bi++) buckets.push([]);

    var width = 0;
    var height = 0;

    /* Wave sources: fixed random positions, each with its own spatial frequency,
     amplitude and angular speed. Four overlapping waves give the interference
     pattern its drifting, organic look. */
    var sources = [];
    var S = 4;
    for (var i = 0; i < S; i++) {
      sources.push({
        x: 0,
        y: 0,
        frequency: 0.006 + 0.008 * Math.random(),
        amplitude: 0.25 + 0.15 * Math.random(), // toned down from the 0.35–0.6 reference
        phase: Math.random() * Math.PI * 2,
        speed: (0.008 + 0.012 * Math.random()) * 0.45 // radians per 60fps frame (45% of the reference speed)
      });
    }
    var srcAmp = new Float64Array(S);
    for (var k = 0; k < S; k++) srcAmp[k] = sources[k].amplitude;
    var sourcesPlaced = false;

    /* Cell grid, rebuilt on resize: top-left x/y per cell and, per source,
       the constant part of its wave argument (distance * frequency). */
    var cellX, cellY, cellTerms, cellCount;
    function buildCells() {
      var cols = Math.ceil(width / CELL_W);
      var rows = Math.ceil(height / CELL_H);
      cellCount = cols * rows;
      cellX = new Float32Array(cellCount);
      cellY = new Float32Array(cellCount);
      cellTerms = new Float32Array(cellCount * S);
      var c = 0;
      var t = 0;
      for (var row = 0; row < rows; row++) {
        var y = row * CELL_H;
        for (var col = 0; col < cols; col++, c++) {
          var x = col * CELL_W;
          cellX[c] = x;
          cellY[c] = y;
          for (var k = 0; k < S; k++, t++) {
            var dx = x - sources[k].x;
            var dy = y - sources[k].y;
            cellTerms[t] = Math.sqrt(dx * dx + dy * dy) * sources[k].frequency;
          }
        }
      }
    }

    function resize() {
      var parent = canvas.parentElement;
      if (!parent) return;
      width = parent.offsetWidth;
      height = parent.offsetHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      /* Setting width/height resets the context, so re-apply state. */
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.font = FONT;
      ctx.textBaseline = 'top';
      /* Sources are scattered over the first measured size and then stay put
         (moving them on resize would make the pattern jump). */
      if (!sourcesPlaced) {
        for (var k = 0; k < S; k++) {
          sources[k].x = Math.random() * width;
          sources[k].y = Math.random() * height;
        }
        sourcesPlaced = true;
      }
      buildCells();
    }
    resize();
    window.addEventListener('resize', resize);

    var lastTime = 0;
    function render(now) {
      /* Advance phases by wall-clock time so the motion speed is the same
       regardless of frame rate (and after being paused off-screen). */
      var dt = lastTime ? Math.min(now - lastTime, 100) : 16.67;
      lastTime = now;
      var step = dt / 16.67;
      for (var s = 0; s < S; s++) {
        sources[s].phase += sources[s].speed * step;
      }

      ctx.clearRect(0, 0, width, height);

      for (var c = 0, t = 0; c < cellCount; c++) {
        var n = 0;
        for (var k = 0; k < S; k++, t++) {
          n += Math.sin(cellTerms[t] + sources[k].phase) * srcAmp[k];
        }
        /* Normalise to 0..1. The +1.6 offset (reference used +2) shifts the
         midpoint down so fewer cells land in the bright end of the ramp. */
        n = (n + 1.6) / 4;
        if (n < 0) n = 0;
        else if (n > 1) n = 1;
        var level = Math.round(n * (LEVELS - 1));
        if (levelEmpty[level]) continue; // a space — nothing to draw
        buckets[level].push(c);
      }
      for (var l = 0; l < LEVELS; l++) {
        var bucket = buckets[l];
        if (!bucket.length) continue;
        ctx.fillStyle = levelStyle[l];
        var ch = levelChar[l];
        for (var j = 0; j < bucket.length; j++) {
          var i = bucket[j];
          ctx.fillText(ch, cellX[i], cellY[i]);
        }
        bucket.length = 0;
      }
    }

    /* Only animate while the hero is on screen (rAF already stops in hidden
     tabs), and at most 30 frames a second. */
    var frame = null;
    var lastRender = 0;
    function loop(now) {
      if (now - lastRender >= FRAME_INTERVAL) {
        lastRender = now;
        render(now);
      }
      frame = requestAnimationFrame(loop);
    }
    function start() {
      if (frame === null) {
        lastTime = 0;
        frame = requestAnimationFrame(loop);
      }
    }
    function stop() {
      if (frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) start();
            else stop();
          });
        },
        { threshold: 0 }
      ).observe(canvas);
    } else {
      start();
    }

    /* Draw the first frame synchronously so the hero never shows an empty
     canvas before the observer kicks in. */
    render(0);
  }
})();
